import { Injectable } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Program } from '@program/shared/program.model';
import { ProjectType } from '@project/shared/project-type.enum';
import { Project } from '@project/shared/project.model';
import { CurrencyExchange } from '@shared/models/currency-exchange/currency-exchange.model';
import { ReferenceType } from '@shared/references.model';
import { Country } from '@shared/services/country/country.model';
import { Region } from '@shared/services/region/region.model';
import { GreaterThanZeroValidator } from '@shared/validators/greater-than-zero-validator';
import { sortCurrencyTypesForDisplay } from '@shared/utility';

import { isNoProgram, isProgramAnswered, NO_PROGRAM, UNCHOSEN_PROGRAM } from './program-selection.constant';
import { controlNameFor, controlNameForProjectProperty, isControlBacked, RuleFieldKey } from './project-type-rules/rule-field-mapping';
import { ExternalPrefillConfirmation } from './workflows/external-prefill-provider.model';
import { SpecificationFormRule } from './project-type-rules/specification-form-rule';

@Injectable({
  providedIn: 'root'
})
export class SpecificationFormService {
  private _countries: Country[];
  private _regions: Region[];
  private _programs: Program[];
  private _projectStatusTypes: ReferenceType[];
  private _currencyTypes: ReferenceType[];
  private _exchangeRates: CurrencyExchange[];
  public _dirty = false;

  constructor(private _translateService: TranslateService) {}

  set countries(countries: Country[]) {
    this._countries = countries;
  }
  set regions(regions: Region[]) {
    this._regions = regions;
  }
  set programs(programs: Program[]) {
    this._programs = programs;
  }
  set projectStatusTypes(projectStatusTypes: ReferenceType[]) {
    this._projectStatusTypes = projectStatusTypes;
  }
  set currencyTypes(currencyTypes: ReferenceType[]) {
    this._currencyTypes = sortCurrencyTypesForDisplay(currencyTypes, this._translateService);
  }
  set exchangeRates(exchangeRates: CurrencyExchange[]) {
    this._exchangeRates = exchangeRates;
  }
  set dirty(dirty: boolean) {
    this._dirty = dirty;
  }

  get countries() {
    return this._countries;
  }
  get regions() {
    return this._regions;
  }
  get programs() {
    return this._programs;
  }
  get projectStatusTypes() {
    return this._projectStatusTypes;
  }
  get currencyTypes(): ReferenceType[] {
    return this._currencyTypes;
  }
  get exchangeRates() {
    return this._exchangeRates;
  }
  get dirty() {
    return this._dirty;
  }

  public createForm(isDisabled: boolean, specificationFormRule: SpecificationFormRule): UntypedFormGroup {
    const fields = specificationFormRule?.fields;
    const formGroup = new UntypedFormGroup({
      projectTypeControl: new FormControl<ProjectType>({value: null, disabled: true}, {validators: Validators.required})
    });
    if (fields?.name?.display) {
      formGroup.addControl('nameControl', new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.name?.required) {
        formGroup.controls.nameControl.setValidators([Validators.required]);
      }
    }
    if (fields?.description?.display) {
      formGroup.addControl('descriptionControl', new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.description?.required) {
        formGroup.controls.descriptionControl.setValidators([Validators.required]);
      }
    }
    if (fields?.postalCode?.display) {
      formGroup.addControl('postalCodeControl', new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.postalCode?.required) {
        formGroup.controls.postalCodeControl.setValidators([Validators.required]);
      }
    }
    if (fields?.address1?.display) {
      formGroup.addControl('address1Control',  new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.address1?.required) {
        formGroup.controls.address1Control.setValidators([Validators.required]);
      }
    }
    if (fields?.address2?.display) {
      formGroup.addControl('address2Control', new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.address2?.required) {
        formGroup.controls.address2Control.setValidators([Validators.required]);
      }
    }
    if (fields?.city?.display) {
      formGroup.addControl('cityControl', new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.city?.required) {
        formGroup.controls.cityControl.setValidators([Validators.required]);
      }
    }
    if (fields?.state?.display) {
      formGroup.addControl('regionControl', new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.state?.required) {
        formGroup.controls.regionControl.setValidators([Validators.required]);
      }
    }
    if (fields?.country?.display) {
      formGroup.addControl('countryControl', new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.country?.required) {
        formGroup.controls.countryControl.setValidators([Validators.required]);
      }
    }
    if (fields?.currency?.display) {
      formGroup.addControl('currencyControl', new UntypedFormControl({ value: 'USD', disabled: isDisabled }, { updateOn: 'change' }));
      if (fields.currency?.required) {
        formGroup.controls.currencyControl.setValidators([Validators.required]);
      }
    }
    if (fields?.exchangeRate?.display) {
      formGroup.addControl('exchangeRateControl', new UntypedFormControl({ value: 1, disabled: isDisabled }, GreaterThanZeroValidator));
      if (fields.exchangeRate?.required) {
        formGroup.controls.exchangeRateControl.setValidators([Validators.required, GreaterThanZeroValidator]);
      }
    }
    if (fields?.cod?.display) {
      formGroup.addControl('codControl', new UntypedFormControl({ value: null, disabled: isDisabled }));
      if (fields.cod?.required) {
        formGroup.controls.codControl.setValidators([Validators.required]);
      }
    }
    if (fields?.tenderProject?.display) {
      formGroup.addControl('tenderProjectControl', new UntypedFormControl({ value: false, disabled: isDisabled }));
      if (fields.tenderProject?.required) {
        formGroup.controls.tenderProjectControl.setValidators([Validators.required]);
      }
    }
    if (fields?.status?.display) {
      formGroup.addControl('statusControl', new UntypedFormControl({ value: '', disabled: isDisabled }));
      if (fields.status?.required) {
        formGroup.controls.statusControl.setValidators([Validators.required]);
      }
    }
    if (fields?.latitude?.display) {
      formGroup.addControl('latitudeControl', new UntypedFormControl({ value: null, disabled: isDisabled }));
      if (fields.latitude?.required) {
        formGroup.controls.latitudeControl.setValidators([Validators.required]);
      }
    }
    if (fields?.longitude?.display) {
      formGroup.addControl('longitudeControl', new UntypedFormControl({ value: null, disabled: isDisabled }));
      if (fields.longitude?.required) {
        formGroup.controls.longitudeControl.setValidators([Validators.required]);
      }
    }
    if (fields?.program?.display) {
      formGroup.addControl('programControl', new UntypedFormControl({ value: null, disabled: isDisabled }));
      if (fields.program?.required) {
        formGroup.controls?.programControl?.setValidators([Validators.required]);
      }
    }

    this.applyFieldStates(formGroup, specificationFormRule);

    return formGroup;
  }

  /**
   * Re-shapes an EXISTING form group to match a newly-resolved rule. Idempotent; used on a program
   * change and on Change ID.
   *
   * The `FormGroup` instance is never replaced. Both `ProjectSpecificationFormComponent` and
   * `ProjectSpecificationComponent` are `ControlValueAccessor`s whose `writeValue()` calls
   * `setValue()`, which throws on a shape mismatch.
   *
   * Ordering here is load-bearing:
   *   1. `{emitEvent: false}` on EVERY mutation, including add/removeControl. This — not the
   *      dirty/touched restore below — is the mechanism that keeps the unsaved-changes dialog
   *      quiet. The dialog reads `siteFormGroup.dirty`, two CVA layers up; once an emission
   *      escapes, the outer control is marked dirty and `markAsPristine()` on the inner group does
   *      not walk back.
   *   2. `updateValueAndValidity` after validator changes — `setValidators` reassigns without
   *      recomputing status, so a narrowing transition would otherwise leave a stale `required`
   *      error and a permanently disabled Create button.
   *   3. Disabled state re-asserted LAST, because step 2 and any `enable()` above it would
   *      otherwise re-enable a rule-locked control.
   */
  public applyRule(formGroup: UntypedFormGroup, rule: SpecificationFormRule, isDisabled = false): string[] {
    const fields = rule?.fields;
    if (!formGroup || !fields) {
      return [];
    }
    const wasDirty = formGroup.dirty;
    const wasTouched = formGroup.touched;
    const added: string[] = [];

    Object.keys(fields).forEach(key => {
      // `map` and any future display-only flag have no control; creating one would invent a
      // phantom control and leave the group not matching a freshly-built one.
      if (!isControlBacked(key)) {
        return;
      }
      const field = fields[key];
      const controlName = controlNameFor(key as RuleFieldKey);
      const existing = formGroup.controls[controlName];

      if (!field?.display) {
        // projectTypeControl is never removed: getFormValues reads it unguarded on every save.
        if (existing && controlName !== 'projectTypeControl') {
          formGroup.removeControl(controlName, { emitEvent: false });
        }
        return;
      }

      if (!existing) {
        formGroup.addControl(
          controlName,
          new UntypedFormControl({ value: null, disabled: isDisabled }),
          { emitEvent: false }
        );
        // A freshly added control has never been interacted with; leaving it touched would show
        // validation errors on fields the user has not seen yet.
        // {onlySelf: true} is REQUIRED: without it, markAsPristine recomputes the PARENT via
        // _updatePristine(), and a group whose children are all pristine becomes pristine itself —
        // silently discarding a real user edit made before the transition.
        formGroup.controls[controlName].markAsUntouched({ onlySelf: true });
        formGroup.controls[controlName].markAsPristine({ onlySelf: true });
        added.push(controlName);
      }

      const control = formGroup.controls[controlName];
      control.setValidators(field.required ? [Validators.required] : null);
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.applyFieldStates(formGroup, rule);
    formGroup.updateValueAndValidity({ emitEvent: false });

    if (!wasDirty) { formGroup.markAsPristine(); }
    if (!wasTouched) { formGroup.markAsUntouched(); }

    return added;
  }

  /**
   * Applies a confirmed external prefill to the FORM CONTROLS, and carries the token on the project
   * (design FR-10, FR-11, D4).
   *
   * **Into controls, not just the working `Project`.** `getFormValues` builds the save payload by
   * reading `formGroup.controls.*`, and it reads `controls.projectTypeControl.value` UNGUARDED.
   * Patch only the project object and the type never reaches the controls, so the save POSTs
   * `projectType: null` and the server NPEs. `projectType` is therefore applied like any other
   * patched property, and the caller must re-run `mapSetup()` afterwards or the map modal stays
   * pointed at the seeded 10,10 while the form shows the provider's coordinates.
   *
   * `{emitEvent: false}` throughout, for the same reason as `applyRule`: an escaping emission
   * climbs two `ControlValueAccessor` layers and raises the unsaved-changes dialog.
   *
   * The patch is PRESENTATIONAL (design D1). Only the token is sent; the server re-derives the
   * authoritative patch from its own stored record, so a tampered client cannot claim ownership of
   * provider-owned fields.
   *
   * @returns the control names actually written, for the caller to log or re-render
   */
  public applyExternalPrefillPatch(
    formGroup: UntypedFormGroup,
    project: Project,
    confirmation: ExternalPrefillConfirmation
  ): string[] {
    if (!formGroup || !confirmation) {
      return [];
    }
    const applied: string[] = [];

    Object.keys(confirmation.patch ?? {}).forEach(property => {
      const controlName = controlNameForProjectProperty(property);
      const control = controlName ? formGroup.controls[controlName] : undefined;
      if (!control) {
        // A patched property with no control on this form — a rule may legitimately hide it. The
        // server still owns the value; dropping it here only means it is not shown.
        return;
      }
      control.setValue(confirmation.patch[property], { emitEvent: false });
      // The provider is the source, not the user: leaving these dirty would make an untouched
      // form raise the unsaved-changes dialog and paint validation errors.
      control.markAsPristine({ onlySelf: true });
      control.markAsUntouched({ onlySelf: true });
      applied.push(controlName);
    });

    if (project) {
      // The SAVE carries this and nothing else from the confirmation.
      project.externalPrefillToken = confirmation.token;
      if (confirmation.workflowPayload !== undefined) {
        project.workflowPayload = confirmation.workflowPayload;
      }
    }

    formGroup.updateValueAndValidity({ emitEvent: false });
    return applied;
  }

  /**
   * Drops a previously applied external prefill from the project.
   *
   * Clears the transport fields only — the patched control VALUES are left in place, because the
   * user may have edited them and silently blanking a form is worse than keeping values the server
   * will simply treat as user-supplied once the token is gone.
   */
  public clearExternalPrefill(project: Project): void {
    if (!project) {
      return;
    }
    project.externalPrefillToken = undefined;
    project.workflowPayload = undefined;
  }

  /**
   * Re-clears the touched/pristine flags on controls `applyRule` just added.
   *
   * `applyRule` already clears them at creation, but that is too early to stick: `oes-custom-dropdown`
   * and `oes-quill` are `ControlValueAccessor`s that call `onChange()`/`onTouched()` while
   * initialising, which happens on the tick AFTER the control is added and the template renders it.
   * The net effect is a field the user has never seen arriving dirty AND touched, so the reveal
   * paints "This is a required field" under every newly shown control.
   *
   * Call this from a `setTimeout(…, 0)` so it lands after that render. `{onlySelf: true}` keeps the
   * parent's own flags intact — the program selection that triggered the reveal is a genuine user
   * edit, and the group must stay dirty so the unsaved-changes guard still fires.
   */
  public clearInitialInteraction(formGroup: UntypedFormGroup, controlNames: string[]) {
    controlNames?.forEach(name => {
      const control = formGroup?.controls?.[name];
      if (!control) {
        return;
      }
      control.markAsUntouched({ onlySelf: true });
      control.markAsPristine({ onlySelf: true });
    });
  }

  /**
   * Applies the `readOnly` / `fixedValue` field states to already-created controls.
   *
   * A post-pass rather than eighteen more branches above, and idempotent so `applyRule` can reuse
   * it on a program change (FM4).
   *
   * `readOnly` DISABLES the control — it does not remove it. `getFormValues` reads
   * `controls.projectTypeControl.value` and `controls.countryControl.value` directly, so a missing
   * control throws on submit. Disabled controls are excluded from `formGroup.value` but NOT from
   * `formGroup.controls.X.value`, which is what the getters use — so a locked field still reaches
   * the payload, and the server remains the authority for it.
   */
  public applyFieldStates(formGroup: UntypedFormGroup, specificationFormRule: SpecificationFormRule) {
    const fields = specificationFormRule?.fields;
    if (!fields) {
      return;
    }
    Object.keys(fields).forEach(key => {
      const field = fields[key];
      if (!isControlBacked(key) || (!field?.readOnly && field?.fixedValue === undefined)) {
        return;
      }
      const control = formGroup.controls[controlNameFor(key as RuleFieldKey)];
      if (!control) {
        return;
      }
      if (field.fixedValue !== undefined) {
        control.setValue(field.fixedValue, { emitEvent: false });
      }
      control.disable({ emitEvent: false });
    });
  }

  public setFormValues(formGroup: UntypedFormGroup, project: Project, cod: Date, readOnly: boolean, specificationFormRule: SpecificationFormRule) {
    const fields = specificationFormRule?.fields;

    formGroup.controls.projectTypeControl.setValue(project.projectType, {emitEvent: false});
    formGroup.controls.projectTypeControl.disable({emitEvent: false});

    if (fields?.name?.display) {
      formGroup.controls.nameControl.setValue(project.name, {emitEvent: false});
    }
    if (fields?.description?.display) {
      // Description not needed here
    }
    if (fields?.postalCode?.display) {
      formGroup.controls.postalCodeControl.setValue(project.postalCode, {emitEvent: false});
    }
    if (fields?.address1?.display) {
      formGroup.controls.address1Control.setValue(project.address1, {emitEvent: false});
    }
    if (fields?.address2?.display) {
      formGroup.controls.address2Control.setValue(project.address2, {emitEvent: false});
    }
    if (fields?.city?.display) {
      formGroup.controls.cityControl.setValue(project.city, {emitEvent: false});
    }
    if (fields?.state?.display) {
      formGroup.controls.regionControl.setValue(project.region ? project.region.id : null, {emitEvent: false});
    }
    if (fields?.country?.display) {
      formGroup.controls.countryControl.setValue(project.country ? project.country.id : null, {emitEvent: false});
    }
    if (fields?.currency?.display) {
      // default is USD
      if (!project.alternateCurrency) {
        formGroup.controls.currencyControl.setValue('USD', {emitEvent: false});
      } else {
        formGroup.controls.currencyControl.setValue(project.alternateCurrency, {emitEvent: false});
      }
    }
    if (fields?.exchangeRate?.display) {
      // USD = rate is 1
      if (formGroup.controls.currencyControl.value === 'USD') {
        formGroup.controls.exchangeRateControl.setValue(1, {emitEvent: false});
        formGroup.controls.exchangeRateControl.disable({emitEvent: false});
      } else {
        formGroup.controls.exchangeRateControl.setValue(project.exchangeRate, {emitEvent: false});
        // An earlier pass may have left it in the USD lock; a stored non-USD rate is editable.
        // readOnly and rule-owned states are re-applied below.
        formGroup.controls.exchangeRateControl.enable({emitEvent: false});
      }
    }
    if (fields?.cod?.display) {
      formGroup.controls.codControl.setValue(cod, {emitEvent: false});
    }
    if (fields?.tenderProject?.display) {
      formGroup.controls.tenderProjectControl.setValue(project.tenderProject, {emitEvent: false});
    }
    if (fields?.status?.display) {
      formGroup.controls.statusControl.setValue(project.status, {emitEvent: false});
    }
    if (fields?.latitude?.display) {
      formGroup.controls.latitudeControl.setValue(project.latitude, {emitEvent: false});
    }
    if (fields?.longitude?.display) {
      formGroup.controls.longitudeControl.setValue(project.longitude, {emitEvent: false});
    }
    if (fields?.program?.display) {
      // Direction 1 of the bidirectional mapping. A program-less EXISTING project must load as the
      // explicit NO_PROGRAM sentinel, not as null — null is now the UNCHOSEN state, so writing it
      // here would make a saved "No Program" project reopen showing the placeholder and holding the
      // form at stage one.
      formGroup.controls?.programControl?.setValue(
        project.program?.id ? project.program.id : (project.id ? NO_PROGRAM : UNCHOSEN_PROGRAM),
        {emitEvent: false});
    }

    // Re-apply the rule's fixed/read-only states AFTER the project values above, or a stored value
    // would overwrite a fixedValue that was seeded at createForm time.
    //
    // D2 CARVE-OUT: a program's fixed country applies to INTERACTIVE CREATES ONLY. The server
    // enforces it at ProjectController.create / createForProgramOrg and deliberately not on update,
    // so overriding an EXISTING project's stored country here would silently change persisted data
    // and make the client the effective authority regardless of the server rule.
    this.applyFieldStates(formGroup, this.ruleWithoutCreateOnlyFixedValues(specificationFormRule, project));

    formGroup.controls.descriptionControl?.markAsUntouched();
    formGroup.controls.descriptionControl?.markAsPristine();

    if (readOnly) {
      formGroup.disable({emitEvent: false});
    }

  }

  /**
   * Strips `fixedValue` from create-only fields when the project already exists (design D2).
   *
   * `country` is the only such field today: the server applies a program's fixed country at the
   * interactive create entry points and nowhere else, so honoring it on an existing project would
   * rewrite stored data the server never asked to change. `readOnly` is preserved either way — the
   * field still renders locked, it just keeps the project's own value.
   */
  private ruleWithoutCreateOnlyFixedValues(
    specificationFormRule: SpecificationFormRule,
    project: Project
  ): SpecificationFormRule {
    const isExistingProject = !!project?.id;
    if (!isExistingProject || specificationFormRule?.fields?.country?.fixedValue === undefined) {
      return specificationFormRule;
    }
    const fields = { ...specificationFormRule.fields };
    const { fixedValue, ...countryWithoutFixedValue } = fields.country;
    fields.country = countryWithoutFixedValue;
    return { ...specificationFormRule, fields };
  }

  public getFormValues(formGroup: UntypedFormGroup, specificationFormRule: SpecificationFormRule, oldProject?: Project): Project {
    const project = new Project({});
    if (oldProject.id) {
      project.id = oldProject.id;
    }

    const fields = specificationFormRule?.fields;

    project.projectType = formGroup.controls.projectTypeControl.value;

    if (fields?.name?.display) {
      project.name = formGroup.controls.nameControl.value;
    }
    if (fields?.description?.display) {
      if (formGroup.controls.descriptionControl.value.textControl) {
        project.description = formGroup.controls.descriptionControl.value.textControl;
      } else {
        project.description = oldProject.description;
      }
    }
    if (fields?.postalCode?.display) {
      project.postalCode = formGroup.controls.postalCodeControl.value;
    }
    if (fields?.address1?.display) {
      project.address1 = formGroup.controls.address1Control.value;
    }
    if (fields?.address2?.display) {
      project.address2 = formGroup.controls.address2Control.value;
    }
    if (fields?.city?.display) {
      project.city = formGroup.controls.cityControl.value;
    }
    if (fields?.state?.display) {
      const regionId = formGroup.controls.regionControl.value;
      if (regionId && regionId !== '') {
        project.region = this._regions.find(region => region.id === regionId);
      }
    }
    if (fields?.country?.display) {
      const countryId = formGroup.controls.countryControl.value;
    if (countryId && countryId !== '') {
      project.country = this._countries.find(country => country.id === countryId);
    }
    }
    if (fields?.currency?.display) {
      project.alternateCurrency = formGroup.controls.currencyControl.value;
    }
    if (fields?.exchangeRate?.display) {
      project.exchangeRate = formGroup.controls.exchangeRateControl.value;
    }
    if (fields?.cod?.display) {
      project.cod = new Date(formGroup.controls.codControl.value);
    }
    if (fields?.tenderProject?.display) {
      project.tenderProject = formGroup.controls.tenderProjectControl.value;
    }
    if (fields?.status?.display) {
      const statusId = formGroup.controls.statusControl.value;
      if (statusId && statusId !== '') {
        const type = this._projectStatusTypes.find(status => status.id === statusId);
        project.status = type ? type.id : undefined;
      }
    }
    if (fields?.latitude?.display) {
      project.latitude = formGroup.controls.latitudeControl.value;
    }
    if (fields?.longitude?.display) {
      project.longitude = formGroup.controls.longitudeControl.value;
    }
    if (fields?.program?.display) {
      // Direction 2. The sentinel is a UI-only value: it maps back to "no program" and is never
      // looked up as an id, so the Project payload shape is unchanged by this mechanism.
      const programId = formGroup.controls?.programControl?.value;
      if (isProgramAnswered(programId) && !isNoProgram(programId)) {
        project.program = this._programs?.find(program => program.id === programId);
      }
    }

    // External-prefill transport (design FR-10, FR-11, D4). `getFormValues` builds a BRAND NEW
    // `Project`, so anything not copied here is dropped — which is why these are carried over
    // explicitly rather than left on the working project.
    //
    // Only ever set when a provider confirmed a link, so both stay undefined for every project
    // today (the registry ships empty) and an ordinary save is unchanged. The token is an opaque
    // handle to the SERVER-held patch: the client never sends the patched values, and the server
    // re-derives them, so a tampered payload cannot claim provider-owned fields.
    if (oldProject?.externalPrefillToken) {
      project.externalPrefillToken = oldProject.externalPrefillToken;
    }
    if (oldProject?.workflowPayload !== undefined) {
      project.workflowPayload = oldProject.workflowPayload;
    }

    return project;
  }
}
